/**
 * The Civic Pass on its lanyard (screenshots/92–105, spec 98; handoff starter src/auth/LanyardBadge.tsx). Decorative:
 * aria-hidden, and a polite live region says what changed. The form next to it is the source of truth.
 *
 * - Drag the pass: it follows on a short rope; let go and it swings, then settles.
 * - Pull the strap: it stretches (≤ 120 px) and springs back.
 * - What you follow is enamel pins riding on the strap. New pins fly in and the pass jingles.
 *   Tap a pin to open it; drag it off the strap (> 80 px) to unfollow. Both also exist in the form / Settings.
 * - Each newly filled field nudges the pass; at 6/6 the segments merge into one line and the foil sweeps once.
 * - prefers-reduced-motion: no physics, the pass hangs still, pins and fields fade in (100 ms).
 * - The loop stops when everything is at rest, and pauses off-screen and in background tabs.
 * Positions are written to the DOM from the animation frame (never React state, brief §0.6).
 */
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { prefersReducedMotion, recipes, reducedFallback } from "../motion";
import { announce, newPins, PASS_PINS, SEGMENTS, STRAP_PINS, type Pin, type PassView } from "./civicPass";
import { atRest, cardTransform, createLanyard, grabCard, grabStrap, moveGrab, nudge, pinSlot, pointOnStrap, release, step, strapPath, type Lanyard } from "./lanyardPhysics";
import { PinIcon } from "./pinIcons";

export const PASS_W = 340, PASS_H = 500, PUNCH_Y = 18, PIN_SIZE = 32, UNPIN_DISTANCE = 80;
/** Strap at rest: from just above the stage to the punch slot (the clasp covers its last 40 px). */
/** Pins ride on the visible top part of the strap (fractions of its length, screenshots/95, 102). */
const PIN_FROM = 0.24, PIN_TO = 0.77;
export const STRAP_REST = 208, STRAP_PHONE = 74;
const phone = () => typeof matchMedia === "function" && matchMedia("(max-width: 767px)").matches;
const WEAVE: Record<PassView["theme"], string> = { neutral: "CIVIC PASS", resident: "RESIDENT PASS", office: "OFFICE PASS", advocacy: "ADVOCACY PASS" };
const M = recipes.civicPass;

export function LanyardBadge({ view, strap: strapProp, reducedMotion, onPinOpen, onUnpin, bare = false, stamp, anchorY = -10 }: {
  view: PassView;
  bare?: boolean;                       // strap only, no pass (link expired)
  stamp?: string;                       // e.g. "NOT VALID FOR THIS DASHBOARD" (no access)
  strap?: number;                       // strap length at rest, px
  anchorY?: number;                     // top of the strap, px from the stage top
  reducedMotion?: boolean;              // defaults to the OS setting
  onPinOpen?: (pin: Pin) => void;
  onUnpin?: (pin: Pin) => void;
}) {
  const reduced = reducedMotion ?? prefersReducedMotion();
  const strap = strapProp ?? (phone() ? STRAP_PHONE : STRAP_REST);   // phones show the top of the pass under a short strap (99)
  const stage = useRef<HTMLDivElement>(null), card = useRef<HTMLDivElement>(null), path = useRef<SVGPathElement>(null), clip = useRef<HTMLDivElement>(null);
  const pinEls = useRef(new Map<string, HTMLButtonElement>());
  const pinDrag = useRef<{ id: string; x: number; y: number; moved: boolean } | null>(null);
  const sim = useRef<Lanyard | null>(null);
  const raf = useRef<number | null>(null), last = useRef(0), visible = useRef(true);
  const prev = useRef(view);
  const [message, setMessage] = useState("");
  const [issuing, setIssuing] = useState(false);
  const strapPins = view.pins.slice(0, STRAP_PINS);
  const pinsRef = useRef(strapPins); pinsRef.current = strapPins;

  const draw = () => {
    const l = sim.current; if (!l) return;
    if (card.current) card.current.style.transform = cardTransform(l, PASS_W, PUNCH_Y);
    path.current?.setAttribute("d", strapPath(l));
    const pin = l.rope[l.o.links], above = l.rope[l.o.links - 1];
    if (clip.current) clip.current.style.transform = `translate(${pin.x.toFixed(1)}px, ${pin.y.toFixed(1)}px) rotate(${(-Math.atan2(pin.x - above.x, pin.y - above.y) * 57.2958).toFixed(2)}deg)`;
    const pins = pinsRef.current;
    pins.forEach((p, i) => {
      const el = pinEls.current.get(p.id); if (!el) return;
      const d = pinDrag.current?.id === p.id ? pinDrag.current : null;
      const at = d ?? pointOnStrap(l, pinSlot(i, pins.length, PIN_FROM, PIN_TO));
      const tilt = ((i % 2 ? 6 : -8) - ("angle" in at ? at.angle * 57.3 : 0)).toFixed(1);
      el.style.transform = `translate(${(at.x - PIN_SIZE / 2).toFixed(1)}px, ${(at.y - PIN_SIZE / 2).toFixed(1)}px) rotate(${tilt}deg)`;
    });
  };
  const frame = (now: number) => {
    const l = sim.current; if (!l) return;
    step(l, Math.min(0.05, (now - last.current) / 1000 || 1 / 60)); last.current = now; draw();
    raf.current = (!atRest(l) || pinDrag.current) && visible.current ? requestAnimationFrame(frame) : null;
  };
  const kick = () => {
    if (raf.current != null || !visible.current || reduced) { draw(); return; }
    last.current = performance.now(); raf.current = requestAnimationFrame(frame);
  };

  useLayoutEffect(() => {
    const el = stage.current; if (!el) return;
    const build = () => {
      sim.current = createLanyard({ anchor: { x: el.clientWidth / 2 || 412, y: anchorY }, strapLength: strap, cardOffset: PASS_H / 2 - PUNCH_Y });
      for (let i = 0; i < 4; i++) step(sim.current, 1 / 60);   // settle the rope before the first paint
      draw();
    };
    build();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(build) : null; ro?.observe(el);
    return () => ro?.disconnect();
  }, [strap, anchorY]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = stage.current; if (!el) return;
    const io = typeof IntersectionObserver === "function" ? new IntersectionObserver(([e]) => { visible.current = !!e?.isIntersecting; if (visible.current) kick(); }) : null;
    io?.observe(el);
    const onVis = () => { visible.current = document.visibilityState === "visible"; if (visible.current) kick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { io?.disconnect(); document.removeEventListener("visibilitychange", onVis); if (raf.current != null) cancelAnimationFrame(raf.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const before = prev.current; prev.current = view;
    const said = announce(before, view); if (said) setMessage(said);
    draw();
    for (const p of newPins(before, view)) {               // fly-in: from the left, with a spin (composited on top of the strap transform)
      pinEls.current.get(p.id)?.animate?.(reduced
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [{ translate: "-160px 60px", rotate: "-40deg", scale: "1.3", opacity: 0.4 }, { translate: "0 0", rotate: "0deg", scale: "1", opacity: 1 }],
        reduced ? { duration: reducedFallback.duration } : { duration: M.flyIn, easing: M.flyEasing, composite: "add" });
    }
    if (view.filled > before.filled && sim.current && !reduced) { nudge(sim.current, M.nudge * (view.filled % 2 ? 1 : -1)); kick(); }
    if (view.issued && !before.issued) { setIssuing(true); const t = setTimeout(() => setIssuing(false), reduced ? 0 : M.foil); return () => clearTimeout(t); }
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const point = (e: RPointerEvent) => { const r = stage.current!.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const down = (kind: "card" | "strap") => (e: RPointerEvent) => {
    if (reduced || !sim.current) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    (kind === "card" ? grabCard : grabStrap)(sim.current, point(e)); kick();
  };
  const pinDown = (p: Pin) => (e: RPointerEvent) => {
    e.stopPropagation(); (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    pinDrag.current = { id: p.id, ...point(e), moved: false };
    if (reduced) draw(); else kick();
  };
  const move = (e: RPointerEvent) => {
    if (pinDrag.current) {
      const q = point(e), d = pinDrag.current;
      pinDrag.current = { ...d, ...q, moved: d.moved || Math.hypot(q.x - d.x, q.y - d.y) > 4 };
      if (reduced) draw(); else kick();
      return;
    }
    if (sim.current?.grab) { moveGrab(sim.current, point(e)); kick(); }
  };
  const up = () => {
    const d = pinDrag.current;
    if (d) {
      pinDrag.current = null;
      const pins = pinsRef.current, pin = pins.find((p) => p.id === d.id), l = sim.current;
      if (pin && l) {
        const home = pointOnStrap(l, pinSlot(pins.indexOf(pin), pins.length, PIN_FROM, PIN_TO));
        if (!d.moved) onPinOpen?.(pin);
        else if (Math.hypot(d.x - home.x, d.y - home.y) > UNPIN_DISTANCE) onUnpin?.(pin);
      }
      kick(); return;
    }
    if (sim.current?.grab) { release(sim.current); kick(); }
  };

  return (
    <div className={`lanyard is-${view.theme}${reduced ? " is-static" : ""}`} ref={stage} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      <svg className="lanyard__strap" aria-hidden width="100%" height="100%">
        <defs><path id="lanyard-path" ref={path} d="" /></defs>
        <use href="#lanyard-path" className="lanyard__band" onPointerDown={down("strap")} />
        <text className="lanyard__weave"><textPath href="#lanyard-path" startOffset="14">{`CHICAGO POLITICS ✶ ${WEAVE[view.theme]} ✶ `.repeat(3)}</textPath></text>
      </svg>
      {!bare && <div className={`civic-pass is-${view.theme}${view.issued ? " is-issued" : ""}${issuing ? " is-issuing" : ""}`} ref={card} aria-hidden
        style={{ width: PASS_W, height: PASS_H, transformOrigin: `${PASS_W / 2}px ${PUNCH_Y}px` }} onPointerDown={down("card")}>
        <PassCard view={view} />
        {stamp && <span className="civic-pass__stamp">{stamp}</span>}
      </div>}
      <div className="lanyard__clip" ref={clip} aria-hidden><span className="lanyard__clasp" /><span className="lanyard__ring" /></div>
      {strapPins.map((p) => (
        <button key={p.id} type="button" tabIndex={-1} aria-hidden className={`pin is-${p.kind}`} style={{ background: p.color }}
          ref={(el) => { if (el) pinEls.current.set(p.id, el); else pinEls.current.delete(p.id); }} onPointerDown={pinDown(p)}>
          <PinIcon name={p.icon} size={15} />
        </button>
      ))}
      <p className="sr-only" aria-live="polite">{message}</p>
    </div>
  );
}

/** The card face (also used on its own, e.g. a pass in Settings → Your pass). */
export function PassCard({ view }: { view: PassView }) {
  const shown = view.pins.slice(0, PASS_PINS), more = view.pins.length - shown.length;
  return (
    <>
      <span className="civic-pass__punch" />
      {view.watermark && <span className="civic-pass__mark">{view.watermark}</span>}
      <header className="civic-pass__head"><img src="/dashboard/brand/chicago-politics.png" alt="" width={118} height={47} draggable={false} />
        <span><b>{view.kind}</b><small>{view.number}</small></span></header>
      <div className="civic-pass__id">
        <span className={`civic-pass__photo${view.initials ? " is-set" : ""}`}>{view.initials ?? <PinIcon name="user-round" size={30} />}</span>
        <span className="civic-pass__who"><strong className={view.name ? "" : "is-ghost"}>{view.name ?? "Your name"}</strong>
          <em className={`civic-pass__role${view.role ? " is-set" : ""}`}>{view.role ?? "Who are you here as?"}</em></span>
      </div>
      <dl className="civic-pass__fields">
        {view.fields.map((f) => (
          <div key={f.label}><dt>{f.label}</dt><dd className={f.error ? "is-error" : undefined}>
            {f.label === "Following"
              ? (shown.length ? <span className="civic-pass__pins">{shown.map((p) => <i key={p.id} style={{ background: p.color }}><PinIcon name={p.icon} size={13} /></i>)}{more > 0 && <b>+{more}</b>}</span> : <span className="civic-pass__ghost" />)
              : f.value ?? <span className="civic-pass__ghost" />}
          </dd></div>))}
      </dl>
      <footer className="civic-pass__foot">
        {view.issued ? <span className="civic-pass__done" /> : <ol className="civic-pass__progress">{Array.from({ length: SEGMENTS }, (_, i) => <li key={i} data-on={i < view.filled || undefined} />)}</ol>}
        <span className="civic-pass__status">{view.status}</span><span className="civic-pass__barcode" />
      </footer>
      <span className="civic-pass__holo" />
    </>
  );
}
